<template>
  <div v-if="enabled" ref="root" class="help-menu" data-testid="help-menu">
    <EXTooltip title="Help" placement="bottom" :delay="200" :disabled="isOpen">
      <button
        ref="trigger"
        type="button"
        class="help-menu__button"
        data-testid="help-menu-button"
        aria-label="Help"
        aria-haspopup="true"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-controls="panelId"
        @click="toggle"
        @keydown.enter.prevent="toggle"
      >
        <svg
          class="help-menu__icon"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13M12 17H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </EXTooltip>
    <div
      v-if="isOpen"
      :id="panelId"
      class="help-menu__panel"
      role="menu"
      data-testid="help-menu-panel"
    >
      <span class="help-menu__tip" aria-hidden="true" />
      <a
        class="help-menu__item"
        role="menuitem"
        data-testid="help-menu-user-guide"
        :href="userGuideUrl"
        target="_blank"
        rel="noopener noreferrer"
        @click="close"
      >
        User Guide
      </a>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, getCurrentInstance, nextTick } from "vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { LD_FLAG_IDEA_MDP_631 } from "@components/constants/launchDarklyFlagKeys";
import { USER_GUIDE_URL } from "@components/constants/helpMenu";

const userGuideUrl = USER_GUIDE_URL;
const proxy = getCurrentInstance()?.proxy;
const panelId = `help-menu-panel-${proxy?._uid ?? 0}`;

const enabled = ref(false);
const isOpen = ref(false);
const root = ref(null);
const trigger = ref(null);

const onDocumentClick = (event) => {
  if (isOpen.value && root.value && !root.value.contains(event.target)) {
    isOpen.value = false;
  }
};

const onDocumentKeydown = (event) => {
  if (isOpen.value && event.key === "Escape") {
    isOpen.value = false;
    nextTick(() => trigger.value?.focus());
  }
};

const toggle = () => {
  isOpen.value = !isOpen.value;
};

const close = () => {
  isOpen.value = false;
};

onMounted(async () => {
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onDocumentKeydown);
  try {
    enabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_631, false)) === true;
  } catch {
    enabled.value = false;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("keydown", onDocumentKeydown);
});
</script>

<style scoped>
.help-menu {
  position: relative;
  flex-shrink: 0;
}

.help-menu__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background-color: #f2edff;
  color: #344054;
  cursor: pointer;
}

.help-menu__button:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.help-menu__icon {
  display: block;
}

.help-menu__panel {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  z-index: 10;
  min-width: 156px;
  padding: 4px 0;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background-color: #fcfcfd;
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.2);
}

.help-menu__tip {
  position: absolute;
  top: -7px;
  right: 14px;
  width: 12px;
  height: 12px;
  border-top: 1px solid #eaecf0;
  border-left: 1px solid #eaecf0;
  border-radius: 2px;
  background-color: #fcfcfd;
  transform: rotate(45deg);
}

.help-menu__item {
  display: block;
  padding: 10px 16px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  text-decoration: none;
  white-space: nowrap;
}

.help-menu__item:hover {
  background-color: #f9fafb;
}

.help-menu__item:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}
</style>
